import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Accounting from "accounting";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrencyConstants } from "../../Components/CurrencyFormatter";
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import PlanGroupActions from "../../Redux/transition/plan-group/plan-group.reducer";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";
import { CustomerSearchPicker } from "../../Components/CustomerSearchField";
import * as api from "../../Service/api";
import "../../Styles/pages/paymentCalendar.scss";
import "../../Styles/pages/customers.scss";
import "../../Styles/components/tags.scss";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_CHIP_LIMIT = 3;
const DAY_PAGE_SIZE = 100;
const DEFAULT_CHIP_COLOR = "#1a73e8";
const GROUP_COLORS = ["#039be5", "#33b679", "#f6bf26", "#8e24aa", "#d50000", "#0b8043", "#3f51b5", "#e67c73"];

const formatMoney = (amount, symbol) =>
	Accounting.formatMoney(Number(amount) || 0, {
		symbol: symbol || "GHS",
		format: "%s %v",
		thousand: ",",
		decimal: ".",
		precision: 2,
	});

const formatCompactMoney = (amount, symbol) =>
	Accounting.formatMoney(Number(amount) || 0, {
		symbol: symbol || "GHS",
		format: "%s %v",
		thousand: ",",
		decimal: ".",
		precision: Number.isInteger(Number(amount)) ? 0 : 2,
	});

const toKey = (date) => moment(date).format("YYYY-MM-DD");

const startOfSunday = (date) => date.clone().startOf("day").subtract(date.day(), "days");

const startOfCalendarGrid = (visibleMonth) => startOfSunday(visibleMonth.clone().startOf("month"));

const buildMonthCells = (visibleMonth) => {
	const cells = [];
	const cursor = startOfCalendarGrid(visibleMonth);
	for (let i = 0; i < 42; i += 1) {
		cells.push(cursor.clone());
		cursor.add(1, "day");
	}
	return cells;
};

const activateOnEnter = (event, action) => {
	if (event.key === "Enter" || event.key === " ") {
		event.preventDefault();
		action();
	}
};

const pageContent = (data) => {
	if (Array.isArray(data)) {
		return data;
	}
	return data?.content || [];
};

const emptyCalendar = () => ({ days: [], totalCount: 0, totalAmount: 0 });

const asDayChip = (day, source) => ({
	sourceId: source.id,
	count: day.count || 0,
	amount: day.amount || 0,
	color: source.color,
	groupName: source.groupName,
});

const normalizeCalendar = (data, source) => {
	const days = (data?.days || []).map((day) => ({
		date: day.date,
		count: day.count || 0,
		amount: day.amount || 0,
		bySource: [asDayChip(day, source)],
	}));
	return {
		days,
		totalCount: data?.totalCount || 0,
		totalAmount: data?.totalAmount || 0,
	};
};

const mergeCalendars = (payloads) => {
	const byDate = {};
	let totalCount = 0;
	let totalAmount = 0;
	payloads.forEach(({ data, source }) => {
		(data?.days || []).forEach((day) => {
			if (!byDate[day.date]) {
				byDate[day.date] = { date: day.date, count: 0, amount: 0, bySource: [] };
			}
			byDate[day.date].count += day.count || 0;
			byDate[day.date].amount += day.amount || 0;
			byDate[day.date].bySource.push(asDayChip(day, source));
			totalCount += day.count || 0;
			totalAmount += day.amount || 0;
		});
	});
	return {
		days: Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)),
		totalCount,
		totalAmount,
	};
};

const ALL_SOURCE = { id: "all", groupName: "Pending", color: DEFAULT_CHIP_COLOR };

const PaymentCalendar = () => {
	const { t } = useTranslation("common");
	const { currencySymbol } = useCurrencyConstants();
	const symbol = currencySymbol || "GHS";
	const today = useMemo(() => moment().startOf("day"), []);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const defMembership = useSelector((state) => state.defMembership.defMembership);
	const planGroupsGraphedActive = useSelector((state) => state.planGroups.planGroupsGraphedActive);
	const exactFilteredBusinessCustomers = useSelector((state) => state.businessCustomers.exactFilteredBusinessCustomers);
	const fetchingExactFilteredBusinessCustomers = useSelector((state) => state.businessCustomers.fetchingExactFilteredBusinessCustomers);

	const getMembership = useCallback(() => dispatch(DefMembershipActions.defMembershipRequest()), [dispatch]);
	const getActivePlanGroups = useCallback(
		(businessId) => dispatch(PlanGroupActions.planGroupGraphedActiveRequest(businessId)),
		[dispatch]
	);
	const getExactFilteredBusinessCustomers = useCallback(
		(businessId, query, options) =>
			dispatch(BusinessCustomersActions.exactFilteredBusinessCustomerRequest(businessId, query, options)),
		[dispatch]
	);

	const [visibleMonth, setVisibleMonth] = useState(today.clone().startOf("month"));
	const [selectedDate, setSelectedDate] = useState(today.clone());
	const [view, setView] = useState("month");
	const [enabledGroupIds, setEnabledGroupIds] = useState(() => new Set());
	const [customerQuery, setCustomerQuery] = useState("");
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const [customerSuggestions, setCustomerSuggestions] = useState([]);
	const [customerResultsOpen, setCustomerResultsOpen] = useState(false);
	const [awaitingCustomerResults, setAwaitingCustomerResults] = useState(false);
	const customerSearchPending = useRef(false);
	const [panelOpen, setPanelOpen] = useState(true);
	const [calendar, setCalendar] = useState(emptyCalendar);
	const [fetchingCalendar, setFetchingCalendar] = useState(false);
	const [calendarError, setCalendarError] = useState(false);
	const [daySchedules, setDaySchedules] = useState([]);
	const [fetchingDay, setFetchingDay] = useState(false);

	const calendarRequestId = useRef(0);
	const dayRequestId = useRef(0);
	const businessId = defMembership?.business?.id;

	const sources = useMemo(() => {
		const groups = planGroupsGraphedActive?.groups || [];
		return groups
			.filter((item) => item?.group?.id)
			.map((item, index) => ({
				id: String(item.group.id),
				groupId: item.group.id,
				groupName: item.group.name,
				color: GROUP_COLORS[index % GROUP_COLORS.length],
			}));
	}, [planGroupsGraphedActive]);

	useEffect(() => {
		if (!defMembership) {
			getMembership();
		}
	}, [defMembership, getMembership]);

	useEffect(() => {
		if (businessId) {
			getActivePlanGroups(businessId);
		}
	}, [businessId, getActivePlanGroups]);

	useEffect(() => {
		if (!sources.length) {
			return;
		}
		setEnabledGroupIds((current) => {
			if (current.size) {
				const next = new Set([...current].filter((id) => sources.some((source) => source.id === id)));
				return next.size ? next : new Set(sources.map((source) => source.id));
			}
			return new Set(sources.map((source) => source.id));
		});
	}, [sources]);

	useEffect(() => {
		if (!customerSearchPending.current || fetchingExactFilteredBusinessCustomers) return;
		customerSearchPending.current = false;
		setAwaitingCustomerResults(false);
		setCustomerSuggestions(exactFilteredBusinessCustomers || []);
	}, [exactFilteredBusinessCustomers, fetchingExactFilteredBusinessCustomers]);

	const selectedSources = useMemo(() => {
		if (!sources.length) {
			return [];
		}
		const enabled = sources.filter((source) => enabledGroupIds.has(source.id));
		if (!enabled.length || enabled.length === sources.length) {
			return sources;
		}
		return enabled;
	}, [sources, enabledGroupIds]);

	const isFilteredSubset = sources.length > 0 && selectedSources.length > 0 && selectedSources.length < sources.length;
	const singleGroupId = selectedSources.length === 1 ? selectedSources[0].groupId : null;
	const customerId = selectedCustomer?.id || null;

	const range = useMemo(() => {
		if (view === "week") {
			const start = startOfSunday(selectedDate);
			return { start, end: start.clone().add(6, "days") };
		}
		const start = startOfCalendarGrid(visibleMonth);
		return { start, end: start.clone().add(41, "days") };
	}, [view, visibleMonth, selectedDate]);

	const fetchParams = useCallback(
		(extra = {}) => ({
			...(customerId ? { bcId: customerId } : {}),
			...extra,
		}),
		[customerId]
	);

	useEffect(() => {
		if (!businessId) {
			return undefined;
		}
		const requestId = ++calendarRequestId.current;
		const startDate = toKey(range.start);
		const endDate = toKey(range.end);

		const load = async () => {
			setFetchingCalendar(true);
			setCalendarError(false);
			try {
				if (isFilteredSubset) {
					const responses = await Promise.all(
						selectedSources.map((source) =>
							api.getPaymentScheduleCalendar(businessId, fetchParams({ startDate, endDate, groupId: source.groupId }))
						)
					);
					if (calendarRequestId.current !== requestId) {
						return;
					}
					if (responses.some((response) => !response.ok)) {
						setCalendarError(true);
						setCalendar(emptyCalendar());
						return;
					}
					setCalendar(mergeCalendars(responses.map((response, index) => ({ data: response.data, source: selectedSources[index] }))));
					return;
				}

				const response = await api.getPaymentScheduleCalendar(
					businessId,
					fetchParams({ startDate, endDate, ...(singleGroupId ? { groupId: singleGroupId } : {}) })
				);
				if (calendarRequestId.current !== requestId) {
					return;
				}
				if (!response.ok) {
					setCalendarError(true);
					setCalendar(emptyCalendar());
					return;
				}
				const source = singleGroupId ? selectedSources[0] || ALL_SOURCE : ALL_SOURCE;
				setCalendar(normalizeCalendar(response.data, source));
			} catch (error) {
				if (calendarRequestId.current === requestId) {
					setCalendarError(true);
					setCalendar(emptyCalendar());
				}
			} finally {
				if (calendarRequestId.current === requestId) {
					setFetchingCalendar(false);
				}
			}
		};

		load();
		return undefined;
	}, [businessId, range, fetchParams, isFilteredSubset, selectedSources, singleGroupId]);

	const selectedKey = toKey(selectedDate);
	const allowedGroupIds = useMemo(() => new Set(selectedSources.map((source) => source.groupId)), [selectedSources]);

	useEffect(() => {
		if (!businessId || !panelOpen) {
			return undefined;
		}
		if (moment(selectedKey).isBefore(today, "day")) {
			setDaySchedules([]);
			setFetchingDay(false);
			return undefined;
		}

		const requestId = ++dayRequestId.current;
		const load = async () => {
			setFetchingDay(true);
			try {
				const response = await api.getPaymentScheduleCalendarDay(
					businessId,
					selectedKey,
					fetchParams({
						page: 0,
						size: DAY_PAGE_SIZE,
						...(singleGroupId ? { groupId: singleGroupId } : {}),
					})
				);
				if (dayRequestId.current !== requestId) {
					return;
				}
				let records = pageContent(response.ok ? response.data : []);
				if (isFilteredSubset) {
					records = records.filter((record) => allowedGroupIds.has(record.groupId));
				}
				setDaySchedules(records);
			} catch (error) {
				if (dayRequestId.current === requestId) {
					setDaySchedules([]);
				}
			} finally {
				if (dayRequestId.current === requestId) {
					setFetchingDay(false);
				}
			}
		};

		load();
		return undefined;
	}, [businessId, panelOpen, selectedKey, today, fetchParams, singleGroupId, isFilteredSubset, allowedGroupIds]);

	const dayMap = useMemo(() => {
		const map = {};
		calendar.days.forEach((day) => {
			map[day.date] = day;
		});
		return map;
	}, [calendar]);

	const monthCalendarDays = calendar.days;
	const selectedDayTotal = daySchedules.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
	const monthCells = useMemo(() => buildMonthCells(visibleMonth), [visibleMonth]);
	const weekDays = useMemo(() => {
		const start = startOfSunday(selectedDate);
		return Array.from({ length: 7 }, (_, index) => start.clone().add(index, "days"));
	}, [selectedDate]);

	const goToday = () => {
		setVisibleMonth(today.clone().startOf("month"));
		setSelectedDate(today.clone());
		setPanelOpen(true);
	};

	const shiftVisible = (amount) => {
		if (view === "week") {
			const next = selectedDate.clone().add(amount, "week");
			setSelectedDate(next);
			setVisibleMonth(next.clone().startOf("month"));
			return;
		}
		const nextMonth = visibleMonth.clone().add(amount, "month");
		setVisibleMonth(nextMonth);
		if (nextMonth.isSame(today, "month")) {
			setSelectedDate(today.clone());
		} else {
			setSelectedDate(nextMonth.clone().startOf("month"));
		}
	};

	const openDay = (date) => {
		setSelectedDate(date.clone());
		setVisibleMonth(date.clone().startOf("month"));
		setPanelOpen(true);
	};

	const toggleSource = (sourceId) => {
		setEnabledGroupIds((current) => {
			const next = new Set(current.size ? current : sources.map((source) => source.id));
			if (next.has(sourceId)) {
				next.delete(sourceId);
			} else {
				next.add(sourceId);
			}
			return next;
		});
	};

	const searchCustomers = () => {
		const query = customerQuery.trim();
		if (query.length < 3 || !businessId) return;
		customerSearchPending.current = true;
		setAwaitingCustomerResults(true);
		setCustomerSuggestions([]);
		setCustomerResultsOpen(true);
		getExactFilteredBusinessCustomers(businessId, query);
	};

	const clearCustomerSearch = () => {
		customerSearchPending.current = false;
		setAwaitingCustomerResults(false);
		setCustomerResultsOpen(false);
		setCustomerQuery("");
		setSelectedCustomer(null);
		setCustomerSuggestions([]);
	};

	const heading =
		view === "week"
			? `${startOfSunday(selectedDate).format("MMM D")} – ${startOfSunday(selectedDate).add(6, "days").format("MMM D, YYYY")}`
			: visibleMonth.format("MMMM YYYY");

	const renderChips = (day, date, limit = MONTH_CHIP_LIMIT) => {
		if (!day?.bySource?.length) {
			return null;
		}
		const visible = day.bySource.slice(0, limit);
		const hiddenCount = day.bySource.length - visible.length;
		return (
			<div className="pc-chips">
				{visible.map((source) => (
					<button
						type="button"
						key={source.sourceId}
						className="pc-chip"
						style={{ background: source.color }}
						onClick={(event) => {
							event.stopPropagation();
							openDay(date);
						}}
					>
						<span>
							{source.count} · {formatCompactMoney(source.amount, symbol)}
						</span>
					</button>
				))}
				{hiddenCount > 0 && (
					<button
						type="button"
						className="pc-chip-more"
						onClick={(event) => {
							event.stopPropagation();
							openDay(date);
						}}
					>
						{t("payment_calendar.more", { count: hiddenCount })}
					</button>
				)}
			</div>
		);
	};

	const sourceColorByGroupId = useMemo(() => {
		const map = {};
		sources.forEach((source) => {
			map[source.groupId] = source.color;
		});
		return map;
	}, [sources]);

	const groupedSelected = useMemo(() => {
		const groups = [];
		daySchedules.forEach((schedule) => {
			const key = schedule.groupId || schedule.groupName || "pending";
			let group = groups.find((item) => item.key === key);
			if (!group) {
				group = {
					key,
					groupName: schedule.groupName || t("payment_calendar.all_products"),
					color: sourceColorByGroupId[schedule.groupId] || DEFAULT_CHIP_COLOR,
					items: [],
				};
				groups.push(group);
			}
			group.items.push(schedule);
		});
		return groups;
	}, [daySchedules, sourceColorByGroupId, t]);

	const allEnabled = !sources.length || selectedSources.length === sources.length;

	return (
		<div className="payment-calendar-page">
			<div className="pc-toolbar">
				<div className="pc-toolbar-left">
					<Button
						className="pc-today-btn p-button-outlined"
						label={t("payment_calendar.today")}
						onClick={goToday}
					/>
					<Button
						icon="pi pi-chevron-left"
						className="pc-nav-btn p-button-text"
						onClick={() => shiftVisible(-1)}
						aria-label={t("payment_calendar.previous")}
					/>
					<Button
						icon="pi pi-chevron-right"
						className="pc-nav-btn p-button-text"
						onClick={() => shiftVisible(1)}
						aria-label={t("payment_calendar.next")}
					/>
					<h1 className="pc-title">{heading}</h1>
				</div>
				<div className="pc-toolbar-right">
					<div className="pc-summary">
						<span className="pc-summary-chip">
							{t("payment_calendar.pending_count", { count: calendar.totalCount })}
						</span>
						<span className="pc-summary-chip">{formatMoney(calendar.totalAmount, symbol)}</span>
					</div>
					<div className="pc-view-switch" role="tablist">
						{["month", "week", "agenda"].map((item) => (
							<button
								type="button"
								key={item}
								className={view === item ? "is-active" : ""}
								onClick={() => setView(item)}
							>
								{t(`payment_calendar.view_${item}`)}
							</button>
						))}
					</div>
				</div>
			</div>

			<div className="pc-body">
				<aside className="pc-sidebar">
					<div className="pc-mini-cal">
						<div className="pc-mini-header">
							<span>{visibleMonth.format("MMMM YYYY")}</span>
							<div className="pc-mini-nav">
								<button type="button" onClick={() => shiftVisible(-1)} aria-label={t("payment_calendar.previous")}>
									<i className="pi pi-chevron-left" />
								</button>
								<button type="button" onClick={() => shiftVisible(1)} aria-label={t("payment_calendar.next")}>
									<i className="pi pi-chevron-right" />
								</button>
							</div>
						</div>
						<div className="pc-mini-weekdays">
							{WEEKDAYS.map((day) => (
								<span key={day}>{day.charAt(0)}</span>
							))}
						</div>
						<div className="pc-mini-grid">
							{monthCells.map((date) => {
								const key = toKey(date);
								const isToday = date.isSame(today, "day");
								const isSelected = date.isSame(selectedDate, "day");
								const isMuted = !date.isSame(visibleMonth, "month");
								return (
									<button
										type="button"
										key={key}
										className={`pc-mini-day${isToday ? " is-today" : ""}${isSelected ? " is-selected" : ""}${isMuted ? " is-muted" : ""}${monthCalendarDays.some((day) => day.date === key) ? " has-events" : ""}`}
										onClick={() => openDay(date)}
									>
										{date.date()}
									</button>
								);
							})}
						</div>
					</div>

					<div className="pc-search">
						<CustomerSearchPicker
							id="payment_calendar_customer_search"
							value={customerQuery}
							placeholder={t("payment_calendar.search_placeholder")}
							suggestions={customerSuggestions}
							open={customerResultsOpen}
							loading={awaitingCustomerResults && customerResultsOpen}
							compact
							emptyLabel={t("common.no_data")}
							onChange={(value) => {
								customerSearchPending.current = false;
								setAwaitingCustomerResults(false);
								setCustomerQuery(value);
								setCustomerResultsOpen(false);
								if (!value) setSelectedCustomer(null);
							}}
							onSearch={searchCustomers}
							onClear={clearCustomerSearch}
							onClose={() => {
								customerSearchPending.current = false;
								setAwaitingCustomerResults(false);
								setCustomerResultsOpen(false);
							}}
							onSelect={(customer) => {
								if (!customer?.id) return;
								setSelectedCustomer(customer);
								setCustomerQuery(customer.name || "");
								setCustomerResultsOpen(false);
							}}
						/>
					</div>

					<div className="pc-sources-label">{t("payment_calendar.my_calendars")}</div>
					{sources.map((source) => {
						const on = allEnabled || enabledGroupIds.has(source.id);
						return (
							<button
								type="button"
								key={source.id}
								className="pc-source-row"
								onClick={() => toggleSource(source.id)}
							>
								<span
									className={`pc-source-swatch${on ? "" : " is-off"}`}
									style={{ background: source.color, color: source.color }}
								>
									{on ? "✓" : ""}
								</span>
								<span>{source.groupName}</span>
							</button>
						);
					})}
				</aside>

				<div className="pc-main">
					<div className="pc-board">
						{(fetchingCalendar && calendar.days.length === 0) && (
							<div className="pc-loading">
								<ProgressSpinner strokeWidth="3" style={{ width: "42px", height: "42px" }} />
							</div>
						)}
						{view !== "agenda" && (
							<div className="pc-weekdays">
								{WEEKDAYS.map((day) => (
									<span key={day}>{day.toUpperCase()}</span>
								))}
							</div>
						)}

						{view === "month" && (
							<div className="pc-month-grid">
								{monthCells.map((date) => {
									const key = toKey(date);
									const day = dayMap[key];
									const isToday = date.isSame(today, "day");
									const isSelected = date.isSame(selectedDate, "day");
									const isOutside = !date.isSame(visibleMonth, "month");
									const isPast = date.isBefore(today, "day");
									return (
										<div
											role="gridcell"
											tabIndex={0}
											key={key}
											className={`pc-day-cell${isToday ? " is-today" : ""}${isSelected ? " is-selected" : ""}${isOutside ? " is-outside" : ""}${isPast ? " is-past" : ""}`}
											onClick={() => openDay(date)}
											onKeyDown={(event) => activateOnEnter(event, () => openDay(date))}
										>
											<div className="pc-day-number">{date.date()}</div>
											{!isPast && renderChips(day, date)}
										</div>
									);
								})}
							</div>
						)}

						{view === "week" && (
							<div className="pc-week-grid">
								{weekDays.map((date) => {
									const key = toKey(date);
									const day = dayMap[key];
									const isToday = date.isSame(today, "day");
									const isSelected = date.isSame(selectedDate, "day");
									const isPast = date.isBefore(today, "day");
									return (
										<div
											role="gridcell"
											tabIndex={0}
											key={key}
											className={`pc-week-col${isToday ? " is-today" : ""}${isSelected ? " is-selected" : ""}`}
											onClick={() => openDay(date)}
											onKeyDown={(event) => activateOnEnter(event, () => openDay(date))}
										>
											<div className="pc-day-number">{date.date()}</div>
											{!isPast && renderChips(day, date, 8)}
										</div>
									);
								})}
							</div>
						)}

						{view === "agenda" && (
							<div className="pc-agenda">
								{!fetchingCalendar && calendar.days.length === 0 && (
									<div className="pc-agenda-empty">
										{calendarError ? t("payment_calendar.load_error") : t("payment_calendar.empty_range")}
									</div>
								)}
								{calendar.days.map((day) => {
									const date = moment(day.date);
									const isToday = date.isSame(today, "day");
									return (
										<div className="pc-agenda-day" key={day.date}>
											<div className={`pc-agenda-date${isToday ? " is-today" : ""}`}>
												<strong>{date.format("D")}</strong>
												<span>{date.format("ddd")}</span>
											</div>
											<div>
												{day.bySource.map((source) => (
													<button
														type="button"
														key={source.sourceId}
														className="pc-agenda-row"
														onClick={() => openDay(date)}
													>
														<span className="pc-agenda-dot" style={{ background: source.color }} />
														<div className="pc-agenda-copy">
															<strong>{source.groupName}</strong>
															<span>{t("payment_calendar.pending_count", { count: source.count })}</span>
														</div>
														<span className="pc-agenda-amount">{formatMoney(source.amount, symbol)}</span>
													</button>
												))}
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>

					{panelOpen && (
						<aside className="pc-panel">
							<div className="pc-panel-header">
								<div>
									<h2>{selectedDate.format("dddd, MMMM D")}</h2>
									<p>
										{daySchedules.length
											? t("payment_calendar.day_summary", {
												count: daySchedules.length,
												amount: formatMoney(selectedDayTotal, symbol),
											})
											: t("payment_calendar.empty_day")}
									</p>
								</div>
								<button
									type="button"
									className="pc-panel-close"
									onClick={() => setPanelOpen(false)}
									aria-label={t("payment_calendar.close")}
								>
									<i className="pi pi-times" />
								</button>
							</div>
							<div className="pc-panel-list">
								{fetchingDay && (
									<div className="pc-panel-loading">
										<ProgressSpinner strokeWidth="3" style={{ width: "32px", height: "32px" }} />
									</div>
								)}
								{!fetchingDay && daySchedules.length === 0 && (
									<div className="pc-empty-day">{t("payment_calendar.empty_day_hint")}</div>
								)}
								{groupedSelected.map((group) => (
									<div className="pc-panel-group" key={group.key}>
										<div className="pc-panel-group-title">
											<span className="pc-agenda-dot" style={{ background: group.color }} />
											{group.groupName}
										</div>
										{group.items.map((schedule) => (
											<button
												type="button"
												className="pc-schedule-card pc-schedule-card-button"
												key={schedule.id}
												onClick={() => {
													if (schedule.busCustomerId) {
														navigate(`/crm/customers/customer/${schedule.busCustomerId}`);
													}
												}}
											>
												<strong>{schedule.customerName}</strong>
												<div className="pc-schedule-meta">
													<span>{schedule.planName}</span>
													<span>{schedule.customerNum}</span>
													<span>{formatMoney(schedule.amount, symbol)}</span>
													<span className="pc-status">{schedule.status}</span>
												</div>
											</button>
										))}
									</div>
								))}
							</div>
						</aside>
					)}
				</div>
			</div>
		</div>
	);
};

export default PaymentCalendar;
